import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Building2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["PLANNED", "ACTIVE", "ON_HOLD", "COMPLETED", "CANCELLED"];
const empty = { client_id: "", code: "", name: "", site_address: "", start_date: "", target_end_date: "", status: "PLANNED" };

export default function Projects() {
  const { user } = useAuth();
  const canEdit = ["ADMIN", "MANAGER"].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [stageOptions, setStageOptions] = useState([]);
  const [selectedStageIds, setSelectedStageIds] = useState([]);
  const navigate = useNavigate();

  const load = () => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/clients").then((r) => setClients(r.data.filter((c) => c.is_active)));
  };
  useEffect(() => { load(); }, []);

  const openDialog = () => {
    api.get("/stage-master").then((r) => {
      const active = r.data.filter((s) => s.is_active);
      setStageOptions(active);
      setSelectedStageIds(active.map((s) => s.id));
    });
    setOpen(true);
  };

  const toggleStage = (id) => {
    setSelectedStageIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const save = async () => {
    try {
      const payload = { ...form, stage_master_ids: selectedStageIds };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = k === "status" ? "PLANNED" : null; });
      payload.status = form.status;
      await api.post("/projects", payload);
      toast.success("Project created");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground mt-1">All interior fit-out engagements.</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" onClick={openDialog} data-testid="new-project-button">
                <Plus className="h-4 w-4 mr-1" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create Project</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Client</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="project-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Code</Label>
                    <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="EXI-2026-014" data-testid="project-code-input" /></div>
                  <div className="space-y-1"><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div className="space-y-1"><Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" /></div>
                <div className="space-y-1"><Label>Site Address</Label>
                  <Input value={form.site_address} onChange={(e) => setForm({ ...form, site_address: e.target.value })} data-testid="project-address-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Start Date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="project-start-input" /></div>
                  <div className="space-y-1"><Label>Target End</Label>
                    <Input type="date" value={form.target_end_date} onChange={(e) => setForm({ ...form, target_end_date: e.target.value })} data-testid="project-target-input" /></div>
                </div>
                <div className="space-y-1">
                  <Label>Stages for this project</Label>
                  <p className="text-xs text-muted-foreground">
                    Uncheck any stage that doesn't apply. The remaining stages' weights
                    are rescaled so progress still reaches 100% when they're all done.
                  </p>
                  <div className="space-y-2 border border-border rounded-md p-3 mt-1">
                    {stageOptions.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No active stages configured yet.</p>
                    ) : stageOptions.map((s) => (
                      <div key={s.id} className="flex items-center gap-2" data-testid={`project-stage-option-${s.id}`}>
                        <Checkbox
                          id={`project-stage-${s.id}`}
                          checked={selectedStageIds.includes(s.id)}
                          onCheckedChange={() => toggleStage(s.id)}
                          data-testid={`project-stage-checkbox-${s.id}`}
                        />
                        <label htmlFor={`project-stage-${s.id}`} className="text-sm cursor-pointer">
                          {s.name} <span className="text-xs text-muted-foreground">({s.weight_pct}%)</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save}
                  disabled={!form.client_id || !form.code || !form.name || selectedStageIds.length === 0}
                  data-testid="save-project-button">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {projects.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-muted-foreground">
          <Building2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
          No projects yet.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <Card key={p.id} className="cursor-pointer hover:shadow-md hover:-translate-y-1 transition-transform duration-200"
              onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-card-${p.id}`}>
              <CardContent className="p-5 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-heading font-semibold">{p.name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{p.code}</div>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
                <div className="text-sm text-muted-foreground">{p.client?.name || "—"}</div>
                <div className="text-xs text-muted-foreground">
                  {p.stage_progress.completed}/{p.stage_progress.total} stages ·{" "}
                  {p.stage_progress.current_stage ? p.stage_progress.current_stage.stage_name : "not started"}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
